/**
 * Module 'onboarding': the first ten minutes (architecture gap analysis 8.4, cost doc 7).
 *
 * Five steps: sign in, this machine connected, connect a repo, start a first mission, pick a
 * notification channel (phone push first). Most of a step's state is derived from what the hub already knows (machines,
 * projects, sessions); the hub module keeps only what it cannot derive (who skipped what,
 * whether the checklist was hidden, the phone's https address).
 *
 *   GET  /api/onboarding          OnboardingState
 *   POST /api/onboarding          OnboardingUpdate, answers OnboardingState
 *   GET  /api/onboarding/reach    HubReach: which addresses the hub listens on, for other machines and phones
 *
 * Browser-safe, no runtime dependencies.
 */
import type { Ts } from '../index.ts';
import { trimEndOf } from './trim.ts';

export type OnboardingStepId = 'signin' | 'machine' | 'repo' | 'mission' | 'phone';

export interface OnboardingStepInfo {
  id: OnboardingStepId;
  title: string;
  /** What finishing it gives you, one line. */
  why: string;
  /** Steps that may be skipped; the others finish only by doing them. */
  skippable: boolean;
}

export const ONBOARDING_STEPS: readonly OnboardingStepInfo[] = [
  { id: 'signin', title: 'Sign in', why: 'Only you, and the people you allow, can see and act.', skippable: false },
  { id: 'machine', title: 'Connect this machine', why: 'Its Claude Code sessions, repos and load report here live.', skippable: false },
  { id: 'repo', title: 'Connect a repo', why: 'Its worktrees, sessions and pipeline show up across machines.', skippable: true },
  { id: 'mission', title: 'Start a first mission', why: 'An agent works on a real task while you watch it here.', skippable: true },
  // The id stays 'phone' (saved marks, ?step=phone links); the step asks for any channel, phone push first (R-HL-2).
  { id: 'phone', title: 'Pick a notification channel', why: 'Requests and failures reach you with the console closed.', skippable: true },
];

export const ONBOARDING_STEP_IDS: readonly OnboardingStepId[] = ONBOARDING_STEPS.map((s) => s.id);

export function isOnboardingStepId(v: unknown): v is OnboardingStepId {
  return typeof v === 'string' && (ONBOARDING_STEP_IDS as readonly string[]).includes(v);
}

export interface OnboardingMark {
  how: 'done' | 'skipped';
  at: Ts;
  by: string;
}

/** What the hub keeps (state.ext.onboarding). Everything else is derived from hub state. */
export interface OnboardingState {
  /** First time someone opened Getting started. */
  startedAt: Ts | null;
  /** The checklist on Overview was hidden. Getting started in Settings brings it back. */
  dismissedAt: Ts | null;
  /** Steps a person marked done or skipped. */
  marks: Partial<Record<OnboardingStepId, OnboardingMark>>;
  /** The https address a phone opens, saved from the phone step. */
  phoneUrl: string | null;
  updatedAt: Ts | null;
  /** When the hub first saw each milestone after Getting started opened; absent until it saw one. */
  journey?: OnboardingJourney;
}

/**
 * R-ON-1: the first-run journey, timed by the hub so every usability test reads the same clock.
 * Both times are when the hub first saw the milestone, never earlier than `startedAt`.
 */
export interface OnboardingJourney {
  /** A machine was online. */
  firstMachineAt: Ts | null;
  /** A session was running (any status but ended) on a machine, so the person could see it here. */
  firstSessionAt: Ts | null;
}

/** R-ON-1: from opening Orgus to a running session, at most this long (proposed, owner to confirm). */
export const ONBOARDING_TARGET_MS = 5 * 60_000;

/** What the hub sees now, for the journey's milestones. */
export interface JourneyFacts {
  machinesOnline: number;
  liveSessions: number;
}

/**
 * The journey after one look at the hub: milestones seen for the first time are stamped `now`.
 * Null when nothing changes (not started yet, or nothing new), so the hub writes only real changes.
 */
export function advanceJourney(s: OnboardingState, f: JourneyFacts, now: Ts): OnboardingJourney | null {
  if (s.startedAt == null) return null;
  const j: OnboardingJourney = s.journey ?? { firstMachineAt: null, firstSessionAt: null };
  const at = Math.max(now, s.startedAt);
  const machine = j.firstMachineAt ?? (f.machinesOnline > 0 || f.liveSessions > 0 ? at : null);
  const session = j.firstSessionAt ?? (f.liveSessions > 0 ? at : null);
  if (machine === j.firstMachineAt && session === j.firstSessionAt) return null;
  return { firstMachineAt: machine, firstSessionAt: session };
}

/** True while the hub still has a milestone to watch for. */
export function journeyOpen(s: OnboardingState): boolean {
  return s.startedAt != null && s.journey?.firstSessionAt == null;
}

export interface JourneyTime {
  /** From opening Getting started to the first running session. */
  ms: number;
  withinTarget: boolean;
}

/** How long the journey took, or null while no session has run yet. */
export function journeyTime(s: Pick<OnboardingState, 'startedAt' | 'journey'>): JourneyTime | null {
  const end = s.journey?.firstSessionAt;
  if (s.startedAt == null || end == null) return null;
  const ms = Math.max(0, end - s.startedAt);
  return { ms, withinTarget: ms <= ONBOARDING_TARGET_MS };
}

/** Other modules a step uses when the hub has them. */
export const ONBOARDING_MODULES = ['auth', 'missions', 'notify', 'templates'] as const;
export type OnboardingModule = (typeof ONBOARDING_MODULES)[number];

/** GET /api/onboarding: the state, plus which of those modules this hub runs (so the page asks only those). */
export interface OnboardingResponse extends OnboardingState {
  modules: Record<OnboardingModule, boolean>;
}

export function emptyOnboardingState(): OnboardingState {
  return { startedAt: null, dismissedAt: null, marks: {}, phoneUrl: null, updatedAt: null };
}

/** POST /api/onboarding */
export type OnboardingUpdate =
  | { op: 'start' }
  | { op: 'dismiss' }
  | { op: 'reopen' }
  | { op: 'mark'; step: OnboardingStepId; how: 'done' | 'skipped' }
  | { op: 'unmark'; step: OnboardingStepId }
  | { op: 'phone-url'; url: string | null }
  | { op: 'reset' };

/** Audit labels for the changes this module records (machine 'hub'); one MODULE_LABELS line in the web's audit labels. */
export const ONBOARDING_ACTION_LABEL: Record<string, string> = {
  'onboarding.phone-url': 'Set phone address',
  'onboarding.reset': 'Start Getting started over',
};

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

export function onboardingUpdateError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the change as a JSON object.';
  switch (v.op) {
    case 'start':
    case 'dismiss':
    case 'reopen':
    case 'reset':
      return null;
    case 'mark':
      if (!isOnboardingStepId(v.step)) return 'Name one of the five steps.';
      if (v.how !== 'done' && v.how !== 'skipped') return 'A step is marked done or skipped.';
      if (v.how === 'skipped' && !ONBOARDING_STEPS.find((s) => s.id === v.step)?.skippable) return 'That step cannot be skipped.';
      return null;
    case 'unmark':
      return isOnboardingStepId(v.step) ? null : 'Name one of the five steps.';
    case 'phone-url':
      if (v.url === null) return null;
      return typeof v.url === 'string' ? phoneUrlError(v.url) : 'Send the address as text, or null to clear it.';
    default:
      return 'Unknown change. Use start, dismiss, reopen, mark, unmark, phone-url or reset.';
  }
}

/** Apply an update (already validated). Pure: the hub persists the result. */
export function applyOnboardingUpdate(s: OnboardingState, u: OnboardingUpdate, by: string, now: Ts): OnboardingState {
  const next: OnboardingState = { ...s, marks: { ...s.marks }, updatedAt: now };
  switch (u.op) {
    case 'start':
      if (next.startedAt == null) next.startedAt = now;
      return next;
    case 'dismiss':
      next.dismissedAt = now;
      return next;
    case 'reopen':
      next.dismissedAt = null;
      if (next.startedAt == null) next.startedAt = now;
      return next;
    case 'mark':
      next.marks[u.step] = { how: u.how, at: now, by };
      return next;
    case 'unmark':
      delete next.marks[u.step];
      return next;
    case 'phone-url':
      next.phoneUrl = u.url ? normalizeUrl(u.url) : null;
      return next;
    case 'reset':
      return { ...emptyOnboardingState(), updatedAt: now };
  }
}

// ---------------------------------------------------------------------------
// Deriving the checklist
// ---------------------------------------------------------------------------

/** What the browser knows from hub state; each module that is missing leaves its count at 0. */
export interface OnboardingFacts {
  machinesOnline: number;
  machinesTotal: number;
  projects: number;
  /** Repos the agents found as the folder of a recent Claude Code session. */
  suggestions: number;
  /** Sessions Orgus started (origin 'orgus'). */
  launchedSessions: number;
  /** Missions, when the missions module is installed. */
  missions: number;
  /** Push channels registered from a phone, when the notify module is installed. */
  phoneDevices: number;
  /** Enabled notification channels of any kind (push, Slack, Teams, email, webhook), phones included. */
  channels: number;
}

export type OnboardingStepStatus = 'done' | 'skipped' | 'current' | 'todo';

export interface OnboardingStepView extends OnboardingStepInfo {
  status: OnboardingStepStatus;
  /** One line on where it stands: '1 machine online', 'Skipped'. */
  detail: string;
}

export interface OnboardingView {
  steps: OnboardingStepView[];
  /** Steps done or skipped. */
  done: number;
  total: number;
  complete: boolean;
  /** The first step still to do, or null when complete. */
  current: OnboardingStepId | null;
  /** A hub with no projects, or with only its own machine. */
  fresh: boolean;
  /** Show the checklist on Overview. */
  showChecklist: boolean;
  /** Open Getting started by itself: a brand new hub nobody has walked through yet. */
  autoOpen: boolean;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A hub with no projects, or one where only its own machine has ever connected. */
export function isFreshHub(f: Pick<OnboardingFacts, 'projects' | 'machinesTotal'>): boolean {
  return f.projects === 0 || f.machinesTotal <= 1;
}

export function deriveOnboarding(f: OnboardingFacts, s: OnboardingState): OnboardingView {
  const derived: Record<OnboardingStepId, { done: boolean; detail: string }> = {
    signin: { done: true, detail: 'Signed in' },
    machine: {
      done: f.machinesOnline > 0,
      detail: f.machinesOnline > 0 ? `${plural(f.machinesOnline, 'machine')} online` : f.machinesTotal > 0 ? 'No machine is reporting' : 'Waiting for the first machine',
    },
    // A mission runs in a repo, so one started means a repo was connected, even after its machine was removed (R-FN-8).
    repo: {
      done: f.projects > 0 || f.missions > 0,
      detail: f.projects > 0 ? `${plural(f.projects, 'project')} watched` : f.missions > 0 ? 'Done before; no repo is connected now' : f.suggestions > 0 ? `${plural(f.suggestions, 'repo')} found on your machines` : 'No repo yet',
    },
    mission: {
      done: f.missions > 0 || f.launchedSessions > 0,
      detail: f.missions > 0 ? `${plural(f.missions, 'mission')} started` : f.launchedSessions > 0 ? `${plural(f.launchedSessions, 'session')} started from Orgus` : 'Nothing started from Orgus yet',
    },
    phone: {
      done: f.channels > 0 || f.phoneDevices > 0,
      detail:
        f.phoneDevices > 0
          ? `${plural(f.phoneDevices, 'phone')} receiving push`
          : f.channels > 0
            ? `${plural(f.channels, 'channel')} set up`
            : s.phoneUrl
              ? 'Phone address saved, no channel yet'
              : 'No channel yet',
    },
  };
  let current: OnboardingStepId | null = null;
  const steps = ONBOARDING_STEPS.map((info): OnboardingStepView => {
    const d = derived[info.id];
    const mark = s.marks[info.id];
    let status: OnboardingStepStatus;
    let detail = d.detail;
    if (d.done || mark?.how === 'done') status = 'done';
    else if (mark?.how === 'skipped' && info.skippable) {
      status = 'skipped';
      detail = 'Skipped';
    } else if (current == null) {
      status = 'current';
      current = info.id;
    } else status = 'todo';
    if (status === 'done' && !d.done && mark?.how === 'done') detail = 'Marked done';
    return { ...info, status, detail };
  });
  const done = steps.filter((x) => x.status === 'done' || x.status === 'skipped').length;
  const complete = done === steps.length;
  const fresh = isFreshHub(f);
  return {
    steps,
    done,
    total: steps.length,
    complete,
    current,
    fresh,
    showChecklist: !complete && s.dismissedAt == null && (fresh || s.startedAt != null),
    autoOpen: f.projects === 0 && s.startedAt == null && s.dismissedAt == null,
  };
}

// ---------------------------------------------------------------------------
// Reaching the hub from other machines and phones
// ---------------------------------------------------------------------------

export type HubAddressKind = 'loopback' | 'lan' | 'tailscale' | 'proxy';

export interface HubAddress {
  url: string;
  kind: HubAddressKind;
  https: boolean;
  /** The hub answered on it when it tried (null: not tried). */
  reachable: boolean | null;
  /** The network interface it belongs to, when known ('Wi-Fi', 'tailscale0'). */
  iface?: string;
}

/** GET /api/onboarding/reach */
export interface HubReach {
  /** This machine's host name: which machine in the list is the hub's own. */
  hostname: string;
  port: number;
  /** The hub only answers on this machine (bound to loopback). */
  loopbackOnly: boolean;
  addresses: HubAddress[];
  /**
   * Some address other than loopback answered when the hub tried it from this machine. A host
   * firewall can still block other machines: that traffic never passes it, so this cannot see it.
   */
  otherMachines: boolean;
  checkedAt: Ts;
}

/** Tailscale hands out addresses in 100.64.0.0/10. */
export function isTailscaleIp(ip: string): boolean {
  const m = /^100\.(\d+)\.\d+\.\d+$/.exec(ip);
  return !!m && Number(m[1]) >= 64 && Number(m[1]) <= 127;
}

/** The eight 16-bit groups of an IPv6 address (with '::' and a dotted IPv4 tail expanded), or null. */
function ipv6Groups(h: string): number[] | null {
  let s = h;
  const v4 = /^(.*:)(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(s);
  if (v4) {
    const [a, b, c, d] = v4.slice(2).map(Number) as [number, number, number, number];
    if ([a, b, c, d].some((n) => n > 255)) return null;
    s = `${v4[1]}${((a << 8) | b).toString(16)}:${((c << 8) | d).toString(16)}`;
  }
  const halves = s.split('::');
  if (halves.length > 2) return null;
  const head = halves[0] ? halves[0].split(':') : [];
  const tail = halves.length === 2 && halves[1] ? halves[1].split(':') : [];
  const fill = halves.length === 2 ? 8 - head.length - tail.length : 0;
  if (fill < 0 || (halves.length === 2 && fill === 0)) return null;
  const all = [...head, ...Array<string>(fill).fill('0'), ...tail];
  if (all.length !== 8 || all.some((g) => !/^[0-9a-f]{1,4}$/.test(g))) return null;
  return all.map((g) => parseInt(g, 16));
}

/**
 * True for a name no other device can use to reach this hub: localhost, 127.x, ::1, the
 * unspecified 0.0.0.0 and ::, and IPv4-mapped forms of those (::ffff:127.0.0.1, ::ffff:7f00:1).
 */
export function isLoopbackName(host: string): boolean {
  const h = host.replace(/^\[|\]$/g, '').toLowerCase();
  if (h === 'localhost' || h.endsWith('.localhost')) return true;
  if (/^\d+(\.\d+){3}$/.test(h)) return /^127\./.test(h) || /^0+(\.0+){3}$/.test(h);
  if (!h.includes(':')) return false;
  const g = ipv6Groups(h);
  if (!g) return false;
  const zeros = (n: number) => g.slice(0, n).every((x) => x === 0);
  if (zeros(7) && (g[7] === 0 || g[7] === 1)) return true;
  // IPv4-mapped (::ffff:a.b.c.d): loopback 127/8 or unspecified 0.0.0.0.
  return zeros(5) && g[5] === 0xffff && (g[6]! >> 8 === 127 || (g[6] === 0 && g[7] === 0));
}

/**
 * The scheme, host and origin of an absolute http(s) URL, without the URL global (this package
 * has no DOM or Node types). Null when it is not one.
 */
export function parseOrigin(raw: string): { protocol: 'http' | 'https'; hostname: string; origin: string } | null {
  const m = /^(https?):\/\/(?:[^@/?#\s]*@)?(\[[0-9a-f:.]+\]|[^:/?#\s[\]@]+)(?::(\d{1,5}))?(?:[/?#]|$)/i.exec(raw.trim());
  if (!m) return null;
  const protocol = m[1]!.toLowerCase() as 'http' | 'https';
  const hostname = m[2]!.toLowerCase();
  const port = m[3] && Number(m[3]) !== (protocol === 'https' ? 443 : 80) ? `:${Number(m[3])}` : '';
  return { protocol, hostname, origin: `${protocol}://${hostname}${port}` };
}

/** A url without a trailing slash, path, query or hash: just the origin. */
export function normalizeUrl(raw: string): string {
  return parseOrigin(raw)?.origin ?? trimEndOf(raw.trim(), '/');
}

/** Why an address will not work on a phone, or null when it will. */
export function phoneUrlError(raw: string): string | null {
  const u = parseOrigin(raw);
  if (!u) return 'Enter a full address, starting with https://';
  if (u.protocol !== 'https') return 'Phones only install the app and allow push on an https address.';
  if (isLoopbackName(u.hostname)) return 'That address only works on this computer. Use the https address your proxy or Tailscale gives the hub.';
  return null;
}

export interface PhoneAddress {
  /** What the QR code holds, or null when there is nothing a phone can use. */
  url: string | null;
  /** True when it is https and not loopback: install and push will work. */
  ready: boolean;
  /** Where the address came from. */
  source: 'browser' | 'saved' | 'proxy' | 'tailscale' | 'lan' | 'none';
  /** One sentence for the person. */
  note: string;
}

/**
 * The address to put in the phone's QR code, best first: the https address this page is
 * open on, the one saved in onboarding, an https proxy the hub saw, then a plain http
 * address on the network (viewable, but install and push need https).
 */
export function choosePhoneAddress(o: { browserOrigin: string; saved: string | null; reach: HubReach | null }): PhoneAddress {
  const browser = o.browserOrigin ? normalizeUrl(o.browserOrigin) : '';
  if (browser && !phoneUrlError(browser)) {
    return { url: browser, ready: true, source: 'browser', note: 'This page is on https, so your phone can open the same address.' };
  }
  if (o.saved && !phoneUrlError(o.saved)) {
    return { url: normalizeUrl(o.saved), ready: true, source: 'saved', note: 'The https address you saved for the hub.' };
  }
  const addrs = o.reach?.addresses ?? [];
  const proxy = addrs.find((a) => a.kind === 'proxy' && a.https);
  if (proxy) return { url: proxy.url, ready: true, source: 'proxy', note: 'The https address the hub was reached on.' };
  const ts = addrs.find((a) => a.kind === 'tailscale' && a.reachable);
  if (ts) {
    return { url: ts.url, ready: false, source: 'tailscale', note: 'Your phone can open this over Tailscale, but install and push need https: run tailscale serve as shown below.' };
  }
  const lan = addrs.find((a) => a.kind === 'lan' && a.reachable);
  if (lan) {
    return { url: lan.url, ready: false, source: 'lan', note: 'Your phone can open this on the same network, but install and push need an https address.' };
  }
  return { url: null, ready: false, source: 'none', note: 'The hub only answers on this computer, so a phone cannot reach it yet.' };
}

/** A push device label that is a phone or tablet ('Safari on iPhone', 'Chrome on Android'). */
export function isPhoneDevice(label: string | undefined | null): boolean {
  return !!label && /iphone|ipad|android|mobile/i.test(label);
}
